import React from 'react';
import { CheckCircle, XCircle } from 'lucide-react';
import type { OnboardingValidationResults } from '../../../types';

interface RuleResultsTabProps {
  results: OnboardingValidationResults;
}

function formatRuleName(name: string): string {
  return name
    .replace(/^expect_column_/, '')
    .replace(/^expect_table_/, '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, c => c.toUpperCase());
}

export default function RuleResultsTab({ results }: RuleResultsTabProps) {
  const { statistics, rule_results } = results;

  return (
    <div className="space-y-6">
      {/* Stats cards */}
      <div className="grid grid-cols-4 gap-4">
        {[
          { label: 'Total Rules', value: statistics.evaluated_expectations, color: 'text-cargo-text' },
          { label: 'Passed', value: statistics.successful_expectations, color: 'text-cargo-success' },
          { label: 'Failed', value: statistics.unsuccessful_expectations, color: 'text-cargo-error' },
          { label: 'Pass Rate', value: `${statistics.success_percent.toFixed(1)}%`, color: statistics.success_percent >= 90 ? 'text-cargo-success' : statistics.success_percent >= 70 ? 'text-amber-400' : 'text-cargo-error' },
        ].map(({ label, value, color }) => (
          <div key={label} className="bg-cargo-surface border border-cargo-border rounded-xl p-4 text-center">
            <p className="text-xs text-cargo-muted font-medium">{label}</p>
            <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
          </div>
        ))}
      </div>

      {/* Rule results table */}
      <div className="bg-cargo-surface border border-cargo-border rounded-xl overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="bg-cargo-panel border-b border-cargo-border">
              <th className="px-4 py-3 text-left text-xs font-semibold text-cargo-muted">Status</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-cargo-muted">Column</th>
              <th className="px-4 py-3 text-left text-xs font-semibold text-cargo-muted">Rule</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-cargo-muted">Unexpected</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-cargo-muted">Unexpected %</th>
              <th className="px-4 py-3 text-right text-xs font-semibold text-cargo-muted">Total Rows</th>
            </tr>
          </thead>
          <tbody>
            {rule_results.map((rr, i) => (
              <tr key={i} className="border-b border-cargo-border hover:bg-cargo-hover/50 transition-colors">
                <td className="px-4 py-3">
                  {rr.success ? (
                    <span className="inline-flex items-center gap-1 text-cargo-success text-xs font-semibold">
                      <CheckCircle className="w-4 h-4" /> PASS
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-cargo-error text-xs font-semibold">
                      <XCircle className="w-4 h-4" /> FAIL
                    </span>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-cargo-text font-mono">{rr.column || '—'}</td>
                <td className="px-4 py-3 text-sm text-cargo-text">{formatRuleName(rr.rule_name)}</td>
                <td className="px-4 py-3 text-sm text-right text-cargo-text">{rr.unexpected_count}</td>
                <td className="px-4 py-3 text-sm text-right">
                  <span className={rr.unexpected_percent > 0 ? 'text-cargo-error font-medium' : 'text-cargo-muted'}>
                    {(rr.unexpected_percent ?? 0).toFixed(2)}%
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-right text-cargo-muted">{rr.element_count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
